import axios from "axios";
import { BlockBlobClient } from "@azure/storage-blob";

const normalizeUploadUrl = (rawUrl) => {
  if (typeof rawUrl !== "string") {
    throw new Error("Invalid upload URL: expected a string");
  }

  let url = rawUrl.trim();

  // Some backend responses wrap URL in quotes/parentheses, e.g. "(https://...)"
  if (url.startsWith("(")) url = url.slice(1);
  if (url.endsWith(")")) url = url.slice(0, -1);
  if (
    (url.startsWith('"') && url.endsWith('"')) ||
    (url.startsWith("'") && url.endsWith("'"))
  ) {
    url = url.slice(1, -1);
  }

  try {
    return new URL(url).toString();
  } catch {
    throw new Error(`Invalid upload URL after normalization: ${url}`);
  }
};

const resolveUploadPayload = (input) => {
  if (!input) throw new Error("Invalid upload payload: file is required");

  if (input instanceof Blob) {
    return { body: input, size: input.size };
  }

  if (input instanceof ArrayBuffer) {
    return { body: input, size: input.byteLength };
  }

  if (ArrayBuffer.isView(input)) {
    return { body: input, size: input.byteLength };
  }

  if (input?.data instanceof Blob) {
    return { body: input.data, size: input.data.size };
  }

  if (input?.file instanceof Blob) {
    return { body: input.file, size: input.file.size };
  }

  throw new Error("Invalid upload payload: unsupported file type");
};

/**
 * Step 1: Request SAS URL from backend
 */
export const requestUpload = async (payload) => {
  try {
    const response = await axios.post(
      "/workbench/ApplicantRequestUpload",
      payload,
    );

    return response.data; // { uploadUrl, documentId }
  } catch (error) {
    console.error("RequestUpload failed:", error);
    throw error;
  }
};

/**
 * Step 2: Upload file directly to Azure Blob
 */
export const uploadFileToBlob = async (file, uploadUrl, onProgress) => {
  try {
    const { body, size } = resolveUploadPayload(file);
    const normalizedUploadUrl = normalizeUploadUrl(uploadUrl);
    const blockBlobClient = new BlockBlobClient(normalizedUploadUrl);
    await blockBlobClient.uploadData(body, {
      blockSize: 4 * 1024 * 1024, // 4MB
      concurrency: 5,
      onProgress: (ev) => {
        if (onProgress) {
          const percent =
            size > 0 ? Math.min(100, Math.round((ev.loadedBytes / size) * 100)) : 0;
          onProgress(percent);
        }
      },
    });
    return true;
  } catch (error) {
    console.error("Azure upload failed:", error);
  }
};

/**
 * Step 3: Confirm upload with backend
 */
export const confirmUpload = async (documentId) => {
  try {
    await axios.post("/workbench/ApplicantConfirmUpload", {
      documentId,
    });

    return true;
  } catch (error) {
    console.error("ConfirmUpload failed:", error);
    throw error;
  }
};

/**
 * Full upload workflow (optional wrapper)
 */
export const uploadDocument = async (file, metadataPayload, onProgress) => {
  // 1. Request SAS
  const { uploadUrl, documentId } = await requestUpload({
    ...metadataPayload,
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type,
  });

  // 2. Upload to Azure
  await uploadFileToBlob(file, uploadUrl, onProgress);

  // 3. Confirm
  await confirmUpload(documentId);

  return documentId;
};
